import { Box, Typography } from "@mui/material";
import { TablePlaceHolder } from "@app/components/PlaceHolder";
import { useSimulationSimulationJobGetFiftyPercentileListQuery } from "@app/api/carApi.generated";
import { BaseDialog } from "@app/components/BaseDialog";
import {
  CarInfoCell,
  CarInfoHeader,
  CarInfoTable,
} from "@app/components/InfoTable";
import { Fragment } from "react";
import { formatPercentFactor, isOddEven } from "@app/utils";
import { CarPortfolioAssetTableData } from "./usePortfolioGroupVersionData";
import { CarTooltipBox } from "@app/components/TooltipBox";
import { pendoClasses } from "@app/core/thirdParty/pendo";

interface ExpectedReturnItem {
  id: string;
  assetClassName: string;
  cumulativeXYearReturn: number;
  weight: number;
  weightedReturn: number;
}

interface PortfolioGroupFiftyPercentileDialogProps {
  dataId: string;
  moduleId: string;
  tableData: CarPortfolioAssetTableData;
  simulationJobId: string;
  horizonYear: number;

  onApply: () => void;
  onCancel: () => void;
}

export const PortfolioGroupFiftyPercentileDialog = (
  props: PortfolioGroupFiftyPercentileDialogProps,
) => {
  const percentiles = useSimulationSimulationJobGetFiftyPercentileListQuery({
    id: props.simulationJobId,
  });

  const items: ExpectedReturnItem[] = props.tableData.assetRows.level4
    .map((i) => {
      const cumulativeXYearReturn =
        percentiles.data?.find(
          (p) => p.asset_class4 === i.id && p.year === props.horizonYear,
        )?.sim_return_nominal ?? 0;
      const weight =
        i.modules
          .find((md) => md.moduleId === props.moduleId)
          ?.values.find((v) => v.dataId === props.dataId)?.allocation ?? 0;
      return {
        id: i.id,
        assetClassName: i.name,
        cumulativeXYearReturn,
        weight,
        weightedReturn: cumulativeXYearReturn * weight,
      };
    })
    .filter((i) => i.weight > 0);

  const band = props.tableData.bands.find((i) => i.moduleId === props.moduleId);
  const column = band?.columns.find((i) => i.dataId === props.dataId);

  return (
    <BaseDialog
      sx={{ width: "1200px", maxWidth: "100vw" }}
      onClose={props.onCancel}
    >
      <TablePlaceHolder isLoading={percentiles.isLoading} rows={8}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
          }}
        >
          <Typography variant="h2SSemiBold">
            {`Simulation: Expected Nominal Return of ${column?.label}`}
            <CarTooltipBox
              sx={{ display: "inline-block", ml: 1, mb: -0.25 }}
              className={
                pendoClasses.editPortfolioFiftyPercentileExpectedReturn
              }
            />
          </Typography>
          <Typography variant="par02SemiBold" sx={{ mt: 1, fontWeight: 600 }}>
            {`E(R) Time Frame: ${props.horizonYear}`}
          </Typography>
          <Typography variant="par02SemiBold" sx={{ mt: 2, fontWeight: 700 }}>
            {band?.label}
          </Typography>
          <CarInfoTable
            sx={{ mt: 1, gridTemplateColumns: "1.5fr 1fr 1fr 1fr" }}
          >
            <CarInfoHeader text="" />
            <CarInfoHeader
              text={`Cumulative ${props.horizonYear} Year Nominal Return`}
            />
            <CarInfoHeader text="Weight" />
            <CarInfoHeader text="Weighted Return" />
            {items.map((i, idx) => (
              <Fragment key={i.id}>
                <CarInfoCell
                  text={i.assetClassName}
                  justifyContent="start"
                  isOdd={isOddEven(idx)}
                />
                <CarInfoCell
                  text={formatPercentFactor(i.cumulativeXYearReturn, {
                    decimalPlaces: 1,
                  })}
                  isOdd={isOddEven(idx)}
                />
                <CarInfoCell
                  text={formatPercentFactor(i.weight, {
                    decimalPlaces: 1,
                  })}
                  isOdd={isOddEven(idx)}
                />
                <CarInfoCell
                  text={formatPercentFactor(i.weightedReturn, {
                    decimalPlaces: 1,
                  })}
                  isOdd={isOddEven(idx)}
                />
              </Fragment>
            ))}
          </CarInfoTable>
        </Box>
      </TablePlaceHolder>
    </BaseDialog>
  );
};
